<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <oxd-form class="orangehrm-paper-container ohrm-timesheet">
    <div class="orangehrm-timesheet-header">
      <div class="orangehrm-timesheet-header--title">
        <slot name="header-title"></slot>
      </div>
      <div class="orangehrm-timesheet-header--options">
        <slot name="header-options"></slot>
      </div>
    </div>

    <div v-if="loading" class="orangehrm-timesheet-loader">
      <oxd-loading-spinner />
    </div>
    <div
      v-else-if="!loading && !columns"
      class="orangehrm-timesheet-body-message"
    >
      <oxd-alert
        type="warn"
        :show="true"
        :message="$t('time.no_timesheets_found')"
      ></oxd-alert>
    </div>

    <div v-else class="orangehrm-timesheet-body">
      <table :class="tableClasses">
        <thead class="orangehrm-timesheet-table-header">
          <tr class="orangehrm-timesheet-table-header-row">
            <th :class="fixedColumnClasses">員工</th>
            <th colspan="2" class="orangehrm-timesheet-table-header-cell">
              類型
            </th>
            <th colspan="7" class="orangehrm-timesheet-table-header-cell">
              星期
            </th>
            <th :class="fixedColumnClasses">預設時間</th>
            <th class="orangehrm-timesheet-table-header-cell">上班(上半)</th>
            <th class="orangehrm-timesheet-table-header-cell">下班(上半)</th>
            <th class="orangehrm-timesheet-table-header-cell">上班(下半)</th>
            <th class="orangehrm-timesheet-table-header-cell">下班(下半)</th>
          </tr>
        </thead>

        <tbody class="orangehrm-timesheet-table-body">
          <!-- timesheet activities -->
          <tr
            v-for="(record, i) in records"
            :key="record"
            :class="{
              'orangehrm-timesheet-table-body-row': true,
              'alert-border': record.overlap,
            }"
          >
            <td :class="fixedCellClasses">
              <oxd-input-field
                v-model="record.employeeId"
                type="select"
                required
                :disabled="checkIfDisabled(record)"
                :options="employee"
                :rules="rules.range"
              />
            </td>
            <td class="orangehrm-timesheet-checkbox-cell" valign="middle">
              <oxd-input-field
                v-model="record.type"
                type="radio"
                value="全職"
                :disabled="checkIfDisabled(record)"
              />
              <div class="orangehrm-timesheet-checkbox-label type">全職</div>
            </td>
            <td class="orangehrm-timesheet-checkbox-cell" valign="middle">
              <oxd-input-field
                v-model="record.type"
                type="radio"
                value="兼職"
                :disabled="checkIfDisabled(record)"
              />
              <div class="orangehrm-timesheet-checkbox-label type">兼職</div>
            </td>
            <td
              v-for="(wday, index) in wdays"
              :key="index"
              class="orangehrm-timesheet-checkbox-cell"
              valign="middle"
            >
              <oxd-input-field
                v-model="record[wday.model]"
                type="checkbox"
                :disabled="wdayIfDisabled(wday.model)"
              />
              <div
                :class="{
                  'orangehrm-timesheet-checkbox-label': true,
                  active: record[wday.model] == true,
                }"
              >
                {{ wday.label }}
              </div>
            </td>
            <td :class="fixedCellClasses">
              <oxd-input-field
                v-model="record.preset"
                type="select"
                required
                :disabled="checkIfDisabled(record)"
                :options="indexOpts(preset, i)"
                :rules="rules.preset"
              />
            </td>
            <td
              :class="{
                'orangehrm-timesheet-table-body-time': true,
                'alert-border': record.Astart_overlap,
              }"
            >
              <oxd-input-field
                v-model="record.Astart"
                type="input"
                required
                :disabled="checkIfDisabled(record)"
                :rules="rules.range"
                @input="limitToFourDigits"
                @focus="selectAll"
                @click="selectAll"
              />
            </td>
            <td
              :class="{
                'orangehrm-timesheet-table-body-time': true,
                'alert-border': record.Aend_overlap,
              }"
            >
              <oxd-input-field
                v-model="record.Aend"
                type="input"
                required
                :disabled="checkIfDisabled(record)"
                :rules="rules.range"
                @input="limitToFourDigits"
                @focus="selectAll"
                @click="selectAll"
              />
            </td>
            <td
              :class="{
                'orangehrm-timesheet-table-body-time': true,
                'alert-border': record.Bstart_overlap,
              }"
            >
              <oxd-input-field
                v-model="record.Bstart"
                type="input"
                required
                :disabled="checkIfDisabled(record)"
                :rules="rules.range"
                @input="limitToFourDigits"
                @focus="selectAll"
                @click="selectAll"
              />
            </td>
            <td
              :class="{
                'orangehrm-timesheet-table-body-time': true,
                'alert-border': record.Bend_overlap,
              }"
            >
              <input
                v-model="record.Bend"
                type="number"
                required
                :disabled="checkIfDisabled(record)"
                :rules="rules.range"
                @input="limitToFourDigits"
                @focus="selectAll"
                @click="selectAll"
              />
            </td>
            <td
              v-if="!checkIfDisabled(record)"
              class="orangehrm-timesheet-table-body-cell control --flex"
            >
              <oxd-icon-button
                name="plus"
                class="orangehrm-timesheet-icon fill-bg"
                @click="cloneRow(record, i)"
                tabindex="-1"
              />
              <oxd-icon-button
                name="plus"
                class="orangehrm-timesheet-icon"
                @click="addRow(i)"
                tabindex="-1"
              />
              <oxd-icon-button
                name="trash"
                class="orangehrm-timesheet-icon"
                @click="deleteRow(i)"
                tabindex="-1"
              />
            </td>
          </tr>
          <!-- timesheet activities -->

          <!-- add row -->
          <tr
            v-if="!checkIfDisabled(false)"
            class="orangehrm-timesheet-table-body-row"
          >
            <td class="orangehrm-timesheet-table-body-cell --flex">
              <oxd-icon-button
                name="plus"
                class="orangehrm-timesheet-icon"
                @click="addRow(this.records.length)"
              />
              <oxd-text type="subtitle-2">新增</oxd-text>
            </td>
          </tr>
          <!-- add row -->

          <tr
            v-if="records.length === 0"
            class="orangehrm-timesheet-table-body-row"
          >
            <td colspan="9" class="orangehrm-timesheet-table-body-cell">
              {{ $t('general.no_records_found') }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="orangehrm-timesheet-footer">
      <div class="orangehrm-timesheet-footer--options">
        <oxd-button v-if="!checkIfDisabled(false)" label="儲存" @click="save" />
        <oxd-button
          v-if="!checkIfDisabled(false)"
          display-type="secondary"
          label="批量複製"
          @click="onClickSelectWeeks"
        />
        <oxd-button
          v-if="!checkIfDisabled(false)"
          display-type="secondary"
          label="配置OT"
          @click="onClickManualOT"
        />
      </div>
      <div class="orangehrm-timesheet-footer--options">
        <slot name="footer-options"></slot>
      </div>
    </div>

    <timesheet-comment-modal
      v-if="showCommentModal"
      :editable="editable"
      :data="commentModalState"
      :timesheet-id="timesheetId"
      @close="onCommentModalClose"
    ></timesheet-comment-modal>
    <timesheet-select-weeks
      ref="timesheetSelectWeeks"
      :initItems="employee"
      :initTitle="initTitle"
      :initSubtitle="initSubtitle"
    ></timesheet-select-weeks>
    <timesheet-manual-ot
      ref="timesheetManualOT"
      :initItems="employee"
      :initTitle="initTitle"
      :initSubtitle="initSubtitle"
    ></timesheet-manual-ot>
  </oxd-form>
</template>

<script>
import {validSelection} from '@/core/util/validation/rules';
import {parseDate, parseTimeInSeconds} from '@ohrm/core/util/helper/datefns';
import TimesheetCommentModal from '@/orangehrmTimePlugin/components/TimesheetCommentModal.vue';
import {OxdAlert, OxdSpinner} from '@ohrm/oxd';
import {ref, watch} from 'vue';
import useTimesheetAPIs from '@/orangehrmTimePlugin/util/composable/useTimesheetAPIs';
import {APIService} from '@/core/util/services/api.service';
import useToast from '@/core/util/composable/useToast';
import SelectWeeks from '@ohrm/components/dialogs/TimesheetSelectWeeks';
import ManualOT from '@ohrm/components/dialogs/TimesheetManualOT';
export default {
  name: 'Timesheet',

  components: {
    'oxd-alert': OxdAlert,
    'oxd-loading-spinner': OxdSpinner,
    'timesheet-comment-modal': TimesheetCommentModal,
    'timesheet-select-weeks': SelectWeeks,
    'timesheet-manual-ot': ManualOT,
  },

  props: {
    records: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Object,
      required: false,
      default: () => null,
    },
    timesheetSetting: {
      type: Object,
      required: false,
      default: () => null,
    },
    dates: {
      type: Array,
      required: false,
      default: () => null,
    },
    subtotal: {
      type: String,
      required: false,
      default: null,
    },
    editable: {
      type: Boolean,
      default: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    timesheetId: {
      type: Number,
      default: null,
    },
    timesheetType: {
      type: Array,
      default: () => [
        {id: 1, label: '全職'},
        {id: 2, label: '兼職'},
      ],
    },
    employee: {
      type: Array,
      default: () => [],
    },
    preset: {
      type: Array,
      default: () => [],
    },
    locationId: {
      type: Number,
      default: null,
    },
    authUserId: {
      type: Number,
      required: true,
    },
    authUserRoleId: {
      type: Number,
      required: true,
    },
    today: {
      type: String,
      required: true,
      default: null,
    },
    locationTitle: {
      type: String,
      required: false,
      default: '',
    },
    upTimesheet: {
      type: Function, // 接收從父組件傳來的 upTimesheet 函數
      required: true,
    },
  },

  setup(props) {
    const http = new APIService(
      window.appGlobal.baseUrl,
      `/api/v2/time/timesheets`,
    );

    const {
      state,
      fetchTimesheetEntries,
      updateTimesheetEntries,
      clearTimesheetEntries,
      cloneTimesheetEntries,
      addOT,
    } = useTimesheetAPIs(http);
    const {saveSuccess} = useToast();

    const onSave = (payload) => {
      state.isLoading = true;
      updateTimesheetEntries(props.locationId, payload, false)
        .then(() => {
          return saveSuccess();
        })
        .catch(() => {
          // Catch invalid parameter error when submitting without any time
          return saveSuccess();
        });
    };
    const onClear = (payload) => {
      state.isLoading = true;
      clearTimesheetEntries(props.locationId, payload, false)
        .then(() => {
          return saveSuccess();
        })
        .catch(() => {
          // Catch invalid parameter error when submitting without any time
          return saveSuccess();
        });
    };
    const onClone = (payload) => {
      state.isLoading = true;
      cloneTimesheetEntries(props.locationId, payload, false)
        .then((data) => {
          props.upTimesheet(props.locationId.id);
        })
        .catch(() => {
          // Catch invalid parameter error when submitting without any time
          return saveSuccess();
        });
    };

    const onManualOT = (payload) => {
      state.isLoading = true;
      const updatedPayload = {
        ...payload,
        super_id: props.authUserId, // 假设 val 存在于 state 中，如果不是，请替换为实际的值
      };
      addOT(props.locationId, updatedPayload, false)
        .then((data) => {
          console.log('updatedPayloaddata', data);
          if (data.data.rscode == 2) {
            alert(data.data.message);
          } else {
            return saveSuccess();
          }
        })
        .catch(() => {
          // Catch invalid parameter error when submitting without any time
          return saveSuccess();
        });
    };

    // 使用 ref 定義 allow_dates，作為響應式變量
    const allow_dates = ref([]);

    // 監聽 props.dates 的變化並更新 allow_dates
    watch(
      () => props.dates,
      (newDates) => {
        if (newDates) {
          allow_dates.value = newDates.filter((item) => {
            if (props.authUserRoleId == 1) {
              return item;
            }
            const today = new Date(props.today);
            const itemDay = new Date(item.id);
            if (itemDay > today) {
              return item;
            }
          });
        }
      },
      {immediate: true},
    );

    return {
      onSave,
      onClear,
      onClone,
      allow_dates,
      onManualOT,
    };
  },

  emits: ['update:records'],

  data() {
    return {
      focusedField: null,
      showCommentModal: false,
      commentModalState: null,
      rules: {
        project: [
          validSelection,
          (v) => v !== null || this.$t('time.select_a_project'),
        ],
        activity: [
          (v) => v !== null || this.$t('time.select_an_activity'),
          (v) =>
            this.records.filter((record) => record.activity?.id === v?.id)
              .length < 2 || this.$t('time.duplicate_record'),
        ],
        range: [
          (v) => {
            this.rangesOverlap();
            return true;
          },
        ],
        preset: [
          (v) => {
            console.log('preset v', v);
            const updated = this.records.map((record, i) => {
              if (i === v.pos) {
                record.Astart = v.Astart;
                record.Aend = v.Aend;
                record.Bstart = v.Bstart;
                record.Bend = v.Bend;
              }
              return record;
            });
            this.syncRecords(updated);
            return true;
          },
        ],
      },
      initTitle: ref(this.locationTitle),
      initSubtitle: '',
      wdays: [
        {label: '一', model: 'w1', extra: '1'},
        {label: '二', model: 'w2', extra: '2'},
        {label: '三', model: 'w3', extra: '3'},
        {label: '四', model: 'w4', extra: '4'},
        {label: '五', model: 'w5', extra: '5'},
        {label: '六', model: 'w6', extra: '6'},
        {label: '日', model: 'w0', extra: '0'},
      ],
    };
  },

  computed: {
    days() {
      return this.columns ? Object.keys(this.columns) : [];
    },
    dailyTotals() {
      const totals = {};
      for (const date in this.columns) {
        totals[date] = this.records.reduce((acc, record) => {
          const duration = parseTimeInSeconds(record.dates[date]?.duration);
          return duration > 0 ? acc + duration : acc;
        }, 0);
      }
      return totals;
    },
    daysOfWeek() {
      const days = [
        this.$t('general.sun'),
        this.$t('general.mon'),
        this.$t('general.tue'),
        this.$t('general.wed'),
        this.$t('general.thu'),
        this.$t('general.fri'),
        this.$t('general.sat'),
      ];
      return this.days.map((day) => {
        const date = parseDate(day, 'yyyy-MM-dd');
        return {
          id: date.valueOf(),
          day: date.getDate(),
          title: days[date.getDay()],
        };
      });
    },
    tableClasses() {
      return {
        'orangehrm-timesheet-table': true,
        '--editable': this.editable,
      };
    },
    fixedColumnClasses() {
      return {
        'orangehrm-timesheet-table-header-cell': true,
        '--freeze-left': !this.editable,
      };
    },
    fixedCellClasses() {
      return {
        'orangehrm-timesheet-table-body-cell': true,
        '--freeze-left': !this.editable,
      };
    },
  },

  methods: {
    checkIfDisabled(record) {
      if (this.authUserRoleId == 1) {
        return false;
      }
      if (this.authUserRoleId == 2) {
        return true;
      }
      const today = new Date(this.today);
      if (record) {
        for (var i = 0; i < this.wdays.length; i++) {
          if (
            record[this.wdays[i].model] &&
            this.wdayIfDisabled(this.wdays[i].model)
          ) {
            return true;
          }
        }
        return false;
      }
      if (this.dates) {
        const lastDay = new Date(this.dates[6].id);
        return lastDay <= today;
      }
      return true;
    },
    wdayIfDisabled(wday) {
      if (this.authUserRoleId == 1) {
        return false;
      }
      if (this.authUserRoleId == 2) {
        return true;
      }
      const today = new Date(this.today);
      let day = '';
      for (var i = 0; i < this.dates.length; i++) {
        if (wday == 'w' + this.dates[i].extra) {
          day = this.dates[i].id;
          break;
        }
      }
      return new Date(day) <= today;
    },
    logSelectDate(record) {
      console.log('record.selectDate', record.selectDate);
    },
    deleteRow(index) {
      let name = this.records[index].employeeId?.label || '';
      if (confirm('要刪除' + name + '資料？')) {
        const updated = this.records.filter((_, i) => i !== index);
        this.syncRecords(updated);
        this.$nextTick().then(() => {
          if (updated.length === 0) this.addRow();
        });
      }
    },
    addRow(i) {
      const newRecord = {
        selectDate: {id: null, label: null, extra: ''},
        employeeId: {id: null, label: null},
        type: null,
        Astart: null,
        Aend: null,
        Bstart: null,
        Bend: null,
      };
      i++;
      // 使用 splice 在指定位置 i 插入新元素
      const updated = [
        ...this.records.slice(0, i), // 取 i 之前的元素
        newRecord, // 在 i 位置插入新记录
        ...this.records.slice(i), // 取 i 之后的元素
      ];
      this.syncRecords(updated);
    },
    cloneRow(record, i) {
      i++;
      const newRecord = JSON.parse(JSON.stringify(record));
      // 使用 splice 在指定位置 i 插入新元素
      const updated = [
        ...this.records.slice(0, i), // 取 i 之前的元素
        newRecord, // 在 i 位置插入新记录
        ...this.records.slice(i), // 取 i 之后的元素
      ];
      this.syncRecords(updated);
    },
    updateTime($value, index, date) {
      const updated = this.records.map((record, i) => {
        if (i === index) {
          const _date = {
            [date]: {
              date: date,
              duration: $value,
              id: record.dates[date]?.id,
              comment: record.dates[date]?.comment,
            },
          };
          record.dates = {...record.dates, ..._date};
        }
        return record;
      });
      this.syncRecords(updated);
    },
    updateComment(id, comment, index, date) {
      const updated = this.records.map((record, i) => {
        if (i === index) {
          const _date = {
            [date]: {
              id: id,
              date: date,
              comment: comment,
              duration: record.dates[date]?.duration,
            },
          };
          record.dates = {...record.dates, ..._date};
        }
        return record;
      });
      this.syncRecords(updated);
    },
    updateProject($value, index) {
      const updated = this.records.map((record, i) => {
        if (i === index) {
          record.project = $value ? $value : null;
          record.customer = $value?._customer ? $value._customer : null;
        }
        return record;
      });
      this.updateActivity(null, index);
      this.syncRecords(updated);
    },
    updateActivity($value, index) {
      const updated = this.records.map((record, i) => {
        if (i === index) {
          record.activity = $value ? {id: $value.id, name: $value.label} : null;
        }
        return record;
      });
      this.syncRecords(updated);
    },
    syncRecords(updated) {
      // if (!this.editable) return;
      this.$emit('update:records', updated);
    },
    viewComment(record, entry, index, date) {
      if (record.project?.id && record.activity?.id) {
        this.commentModalState = {
          date,
          index,
          id: entry?.id,
          project: record.project,
          activity: record.activity,
          customer: record.customer,
        };
        this.showCommentModal = true;
      } else {
        this.$toast.warn({
          title: this.$t('general.warning'),
          message: this.$t('time.select_a_project_and_an_activity'),
        });
      }
    },
    onCommentModalClose($event) {
      if ($event) {
        const {id, comment} = $event;
        const {index, date} = this.commentModalState;
        this.updateComment(id, comment, index, date);
      }
      this.showCommentModal = false;
      this.commentModalState = null;
    },
    getProject(record) {
      const {project, customer} = record;
      if (project && project.label) {
        return project;
      }
      if (project && customer) {
        return {
          id: project.id,
          label: `${customer.name} - ${project.name}`,
        };
      }
      return null;
    },
    getActivity(activity) {
      return activity ? {id: activity.id, label: activity.name} : null;
    },
    getDuration(entry) {
      // TODO: convert to format from user config
      return entry?.duration ? entry.duration : null;
    },
    getCommentIcon(entry) {
      return entry?.comment ? 'chat-dots-fill' : 'chat-dots';
    },
    isCommentVisible(entry, index, date) {
      if (entry?.comment) return true;
      if (this.editable) {
        return (
          this.focusedField &&
          this.focusedField.index === index &&
          this.focusedField.date === date
        );
      }
      return false;
    },
    save() {
      let message = this.rangesOverlap();
      if (message === true) {
        const updated = this.records.map((record, i) => {
          record.overlap = false;
          return record;
        });
        let data = [];
        for (var key in updated) {
          let range = this.getRange(updated[key]);
          if (!updated[key].employeeId || !updated[key].employeeId.id) {
            message = '請選擇員工';
          } else if (!updated[key].type) {
            message = '請選擇類型';
          } else if (
            !updated[key].w1 &&
            !updated[key].w2 &&
            !updated[key].w3 &&
            !updated[key].w4 &&
            !updated[key].w5 &&
            !updated[key].w6 &&
            !updated[key].w0
          ) {
            message = '請選擇星期';
          } else if (
            range === false ||
            (range.Astart === false && range.Bstart === false)
          ) {
            message = '請選擇時間';
            updated[key].Astart_overlap = true;
            updated[key].Aend_overlap = true;
            updated[key].Bstart_overlap = true;
            updated[key].Bend_overlap = true;
          }
          if (message !== true) {
            updated[key].overlap = true;
            break;
          } else {
            data.push({
              employeeId: updated[key].employeeId.id,
              type: updated[key].type,
              w1: updated[key].w1,
              w2: updated[key].w2,
              w3: updated[key].w3,
              w4: updated[key].w4,
              w5: updated[key].w5,
              w6: updated[key].w6,
              w0: updated[key].w0,
              Astart: updated[key].Astart,
              Aend: updated[key].Aend,
              Bstart: updated[key].Bstart,
              Bend: updated[key].Bend,
              preset: updated[key].preset?.label,
            });
          }
        }
        if (message !== true) {
          alert(message);
        } else {
          this.syncRecords(updated);
          let date = [];
          for (var i in this.dates) {
            date.push(this.dates[i].id);
          }
          this.onSave({
            date: date,
            data: data,
          });
        }
      }
    },
    getPosByTime(w, time) {
      for (var key in this.timesheetSetting[w]) {
        if (this.timesheetSetting[w][key].id == time.id) {
          return parseInt(key);
        }
      }
    },
    getRange(record) {
      let range = [];
      range.Astart = false;
      range.Bstart = false;
      if (!record.type) {
        return false;
      }
      if (!record.employeeId) {
        return false;
      }
      if (
        (!record.Astart || !record.Aend) &&
        (!record.Bstart || !record.Bend)
      ) {
        return false;
      }
      if (
        record.Astart &&
        record.Aend &&
        record.Astart.length === 4 &&
        record.Aend.length === 4
      ) {
        range.Astart = parseInt(record.Astart);
        range.Aend = parseInt(record.Aend);
      }
      if (
        record.Bstart &&
        record.Bend &&
        record.Bstart.length === 4 &&
        record.Bend.length === 4
      ) {
        range.Bstart = parseInt(record.Bstart);
        range.Bend = parseInt(record.Bend);
      }
      return range;
    },
    isOverlap(start1, end1, start2, end2) {
      return Math.max(start1, start2) <= Math.min(end1, end2);
    },
    getHour(time) {
      return parseInt(time.substr(0, 2));
    },
    rangesOverlap() {
      let message = true;
      let message_row = false;
      let message_row1 = false;
      let message_row2 = false;
      let message_row3 = false;
      let message_row4 = false;
      const updated = this.records.map((record, i) => {
        record.overlap = false;
        record.Astart_overlap = false;
        record.Aend_overlap = false;
        record.Bstart_overlap = false;
        record.Bend_overlap = false;
        let range = this.getRange(record);
        if (!range) {
          return record;
        }
        if (record.Astart?.length === 4 && this.getHour(record.Astart) < 0) {
          message = '小時不能少過0';
          record.Astart_overlap = true;
        } else if (
          record.Astart?.length === 4 &&
          this.getHour(record.Astart) > 23
        ) {
          message = '上班小時不能大過23';
          record.Astart_overlap = true;
        } else if (record.Aend?.length === 4 && this.getHour(record.Aend) < 0) {
          message = '小時不能少過0';
          record.Aend_overlap = true;
        } else if (
          record.Aend?.length === 4 &&
          this.getHour(record.Aend) > 27
        ) {
          message = '下班小時不能大過27';
          record.Aend_overlap = true;
        } else if (
          record.Bstart?.length === 4 &&
          this.getHour(record.Bstart) < 0
        ) {
          message = '小時不能少過0';
          record.Bstart_overlap = true;
        } else if (
          record.Bstart?.length === 4 &&
          this.getHour(record.Bstart) > 23
        ) {
          message = '上班小時不能大過23';
          record.Btart_overlap = true;
        } else if (record.Bend?.length === 4 && this.getHour(record.Bend) < 0) {
          message = '小時不能少過0';
          record.Bend_overlap = true;
        } else if (
          record.Bend?.length === 4 &&
          this.getHour(record.Bend) > 27
        ) {
          message = '下班小時不能大過27';
          record.Bend_overlap = true;
        } else if (
          (range.Astart !== false && range.Astart == range.Aend) ||
          (range.Bstart !== false && range.Bstart == range.Bend) ||
          (range.Astart !== false &&
            range.Bstart !== false &&
            this.isOverlap(range.Astart, range.Aend, range.Bstart, range.Bend))
        ) {
          message = '時間重疊';
          message_row = i;
          record.overlap = true;
          if (range.Astart == range.Aend) {
            record.Astart_overlap = true;
            record.Aend_overlap = true;
          }
          if (range.Bstart == range.Bend) {
            record.Bstart_overlap = true;
            record.Bend_overlap = true;
          }
        } else if (range.Astart !== false && range.Astart > range.Aend) {
          message = '結束時間大過開始時間';
          message_row = i;
          record.overlap = true;
          record.Astart_overlap = true;
          record.Aend_overlap = true;
        } else if (range.Bstart !== false && range.Bstart > range.Bend) {
          message = '結束時間大過開始時間';
          message_row = i;
          record.overlap = true;
          record.Bstart_overlap = true;
          record.Bend_overlap = true;
        }
        if (message !== true) {
          return record;
        }
        for (var key in this.records) {
          let rangeb = this.getRange(this.records[key]);
          if (!rangeb || i === parseInt(key)) {
            continue;
          }
          if (record.employeeId.id != this.records[key].employeeId.id) {
            continue;
          }
          if (
            !(
              (record.w1 === true && this.records[key].w1 === true) ||
              (record.w2 === true && this.records[key].w2 === true) ||
              (record.w3 === true && this.records[key].w3 === true) ||
              (record.w4 === true && this.records[key].w4 === true) ||
              (record.w5 === true && this.records[key].w5 === true) ||
              (record.w6 === true && this.records[key].w6 === true) ||
              (record.w0 === true && this.records[key].w0 === true)
            )
          ) {
            continue;
          } else {
            message = '日期重疊';
            message_row = parseInt(key);
            record.overlap = true;
          }
          let hit = false;
          if (range.Astart !== false) {
            hit =
              rangeb.Astart !== false &&
              this.isOverlap(
                range.Astart,
                range.Aend,
                rangeb.Astart,
                rangeb.Aend,
              );
            if (hit) {
              record.Astart_overlap = true;
              record.Aend_overlap = true;
              message_row1 = parseInt(key);
              message_row2 = parseInt(key);
            }
            if (hit === false) {
              hit =
                rangeb.Bstart !== false &&
                this.isOverlap(
                  range.Astart,
                  range.Aend,
                  rangeb.Bstart,
                  rangeb.Bend,
                );
              if (hit) {
                record.Astart_overlap = true;
                record.Aend_overlap = true;
                message_row3 = parseInt(key);
                message_row4 = parseInt(key);
              }
            }
          }
          if (range.Bstart !== false) {
            if (hit === false) {
              hit =
                rangeb.Astart !== false &&
                this.isOverlap(
                  range.Bstart,
                  range.Bend,
                  rangeb.Astart,
                  rangeb.Aend,
                );
              if (hit) {
                record.Bstart_overlap = true;
                record.Bend_overlap = true;
                message_row1 = parseInt(key);
                message_row2 = parseInt(key);
              }
            }
            if (hit === false) {
              hit =
                rangeb.Bstart !== false &&
                this.isOverlap(
                  range.Bstart,
                  range.Bend,
                  rangeb.Bstart,
                  rangeb.Bend,
                );
              if (hit) {
                record.Bstart_overlap = true;
                record.Bend_overlap = true;
                message_row3 = parseInt(key);
                message_row4 = parseInt(key);
              }
            }
          }
          if (hit) {
            message = '時間重疊';
            message_row = parseInt(key);
            record.overlap = true;
          }
        }
        return record;
      });
      if (message !== true) {
        alert(message);
        const updated = this.records.map((record, i) => {
          if (message_row !== false && message_row == i) {
            record.overlap = true;
          }
          if (message_row1 !== false && message_row1 == i) {
            record.Astart_overlap = true;
          }
          if (message_row2 !== false && message_row2 == i) {
            record.Aend_overlap = true;
          }
          if (message_row3 !== false && message_row3 == i) {
            record.Bstart_overlap = true;
          }
          if (message_row4 !== false && message_row4 == i) {
            record.Bend_overlap = true;
          }
          return record;
        });
      }
      this.syncRecords(updated);
      return message;
    },
    onClickSelectWeeks() {
      const firstDay = Object.keys(this.columns)[0];
      console.log('firstDay', firstDay);
      this.initTitle = `複製更表到(${firstDay}) ${this.locationTitle}`;
      this.initSubtitle = [`複製會覆蓋現有更表！`];
      this.$refs.timesheetSelectWeeks.showDialog().then((data) => {
        if (data.selectedWeek !== null) {
          this.onClone({from: data.selectedWeek?.id, to: firstDay});
        }
      });
    },
    onClickManualOT() {
      const firstDay = Object.keys(this.columns)[0];
      console.log('firstDay', firstDay);
      this.initTitle = `配置OT (${this.locationTitle})`;
      this.$refs.timesheetManualOT.showDialog().then((data) => {
        console.log('timesheetManualOT data', data);
        this.onManualOT(data);
      });
    },
    selectAll(event) {
      // 使用 setTimeout 是为了确保在 focus 或 click 事件触发后选择内容
      setTimeout(() => {
        event.target.select();
      }, 0);
    },
    limitToFourDigits(event) {
      // 限制为 4 位数字
      if (event.target.value.length > 4) {
        event.target.value = event.target.value.slice(0, 4);
      }
    },
    indexOpts(options, i) {
      return options.map((item, index) => ({
        ...item,
        pos: i,
      }));
    },
  },
};
</script>

<style src="./timesheet.scss" lang="scss" scoped></style>
<style type="text/css">
.ohrm-timesheet .orangehrm-timesheet-table-body-cell {
  vertical-align: top !important;
  min-width: 100px;
  text-align: center;
}
.ohrm-timesheet .alert-border {
  border: 1px solid #eb0910 !important;
}
.ohrm-timesheet td.alert-border {
  background-color: #eb091025;
}
.ohrm-timesheet .fill-bg {
  background-color: #ff7b1d !important;
  color: #fff !important;
}
.ohrm-timesheet .oxd-checkbox-input {
  width: 2.5rem !important;
  height: 2.5rem !important;
  margin-right: 0.3rem !important;
}
.ohrm-timesheet .orangehrm-timesheet-table-body-time {
  min-width: 60px;
  padding: 0.5rem !important;
}
.ohrm-timesheet .orangehrm-timesheet-table-body-time input {
  box-sizing: border-box;
  color: #64728c;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  border-radius: 0.65rem;
  padding: 0.675rem 0.5rem;
  width: 100%;
  min-height: 45px;
  border: 1px solid #e8eaef;
}
.ohrm-timesheet .orangehrm-timesheet-checkbox-cell:not(.control) {
  padding-left: 5px !important;
  position: relative;
}
.ohrm-timesheet .orangehrm-timesheet-checkbox-cell:not(.control) {
  padding-right: 0px !important;
}
.ohrm-timesheet .orangehrm-timesheet-checkbox-cell label {
  font-size: 16px !important;
  line-height: 2rem !important;
}
.ohrm-timesheet .oxd-checkbox-input-icon {
  opacity: 0 !important;
}
.ohrm-timesheet .orangehrm-timesheet-checkbox-label {
  position: absolute;
  z-index: 2;
  left: 1.25rem;
  top: 1.4rem;
  pointer-events: none;
}
.ohrm-timesheet .orangehrm-timesheet-checkbox-label.active {
  color: white;
}
.ohrm-timesheet .orangehrm-timesheet-checkbox-label.type {
  left: 0.9rem;
}
.ohrm-timesheet .orangehrm-timesheet-table-header-cell {
  border: 1px solid #e8eaef;
  border-radius: 0.65rem;
  text-align: center;
}
.ohrm-timesheet .oxd-radio-input.oxd-radio-input {
  width: 2.5rem;
  height: 2.5rem;
}
.ohrm-timesheet .oxd-radio-wrapper label {
  background-color: transparent !important;
  padding: 0px !important;
}
.ohrm-timesheet input[type='number']::-webkit-outer-spin-button,
.ohrm-timesheet input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
.ohrm-timesheet input[type='number'] {
  -moz-appearance: textfield;
}
</style>
